import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { useImageEditor } from "../components/image-editor/ImageEditorProvider";
import { 
  MessageCircle, 
  Send, 
  Sparkles,
  Users,
  BadgeCheck,
  ArrowLeft,
  ChevronRight,
  Loader2,
  Paperclip,
  X
} from "lucide-react";
import { Button } from "../components/ui/button";

const MessagesPage = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const { token, user, isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [activeArtist, setActiveArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (artistId) {
      fetchMessages(artistId);
      fetchArtistInfo(artistId);
    } else {
      fetchConversations();
    }
  }, [artistId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data);
    } catch (e) {
      console.error("Failed to fetch conversations:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchArtistInfo = async (id) => {
    try {
      const res = await axios.get(`${API}/artists/${id}`);
      setActiveArtist(res.data);
    } catch (e) {
      console.error("Failed to fetch artist:", e);
    }
  };

  const fetchMessages = async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch (e) {
      console.error("Failed to fetch messages:", e);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if ((!messageText.trim() && !attachmentUrl) || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/messages/${artistId}`, {
        text: messageText || (attachmentUrl ? "📎 attachment" : ""),
        image_url: attachmentUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => [...prev, res.data]);
      setMessageText("");
      setAttachmentUrl(null);
    } catch (e) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const editImage = useImageEditor();
  const handleAttachImage = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only images supported");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB)");
      return;
    }
    file = await editImage(file, { maxBytes: 5 * 1024 * 1024 });
    if (!file) { e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload/image?category=message`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      setAttachmentUrl(res.data.url);
      toast.success("Attached");
    } catch (e2) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  // Conversation list view
  if (!artistId) {
    return (
      <div className="min-h-screen bg-[#050810]" data-testid="messages-list-page">
        <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/home" className="flex items-center gap-2">
              <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            </Link>
            <Button onClick={() => navigate("/dashboard")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full">
              Dashboard
            </Button>
          </div>
        </header>

        <main className="pt-24 pb-16 px-6">
          <div className="max-w-2xl mx-auto">
            <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-6">Messages</h1>
            
            {loading ? (
              <div className="flex justify-center py-20"><div className="spinner"></div></div>
            ) : conversations.length === 0 ? (
              <div className="card p-12 text-center">
                <MessageCircle className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
                <h2 className="font-['Unbounded'] text-xl text-white mb-2">No Messages Yet</h2>
                <p className="text-[#8A92A0] mb-6">Visit an artist's profile to start a conversation.</p>
                <Button onClick={() => navigate("/fandom")} className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full">
                  Browse Artists
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <Link
                    key={conv.conversation_id}
                    to={`/messages/${conv.artist_id}`}
                    className="card p-4 flex items-center gap-4 hover:border-[#EC008C]/30 transition-colors"
                    data-testid={`conv-${conv.conversation_id}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-[#1A2030] overflow-hidden flex items-center justify-center flex-shrink-0">
                      {conv.artist?.profile_image ? (
                        <img src={conv.artist.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Users className="w-6 h-6 text-[#8A92A0]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-white font-semibold truncate">{conv.artist?.stage_name || "Artist"}</span>
                        {conv.artist?.is_verified && <BadgeCheck className="w-4 h-4 text-[#EC008C] flex-shrink-0" />}
                      </div>
                      <p className="text-sm text-[#6A7280] truncate">{conv.last_message}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-xs text-[#6A7280]">{conv.last_message_at ? timeAgo(conv.last_message_at) : ""}</span>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 rounded-full bg-[#D14A85] text-white text-xs flex items-center justify-center font-bold">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#8A92A0] flex-shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Chat view
  return (
    <div className="min-h-screen bg-[#050810] flex flex-col" data-testid="messages-chat-page">
      {/* Chat Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate("/messages")} className="text-[#8A92A0] hover:text-white" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          {activeArtist && (
            <Link to={`/artist/${artistId}`} className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-full bg-[#1A2030] overflow-hidden flex items-center justify-center flex-shrink-0">
                {activeArtist.profile_image ? (
                  <img src={activeArtist.profile_image} alt="" className="w-full h-full object-cover" />
                ) : (
                  <Users className="w-5 h-5 text-[#8A92A0]" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-white font-semibold truncate">{activeArtist.stage_name}</span>
                  {activeArtist.is_verified && <BadgeCheck className="w-4 h-4 text-[#EC008C]" />}
                </div>
                <span className="text-xs text-[#6A7280]">{activeArtist.genre}</span>
              </div>
            </Link>
          )}
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 pt-20 pb-20 px-6 overflow-y-auto">
        <div className="max-w-3xl mx-auto space-y-3">
          {loading ? (
            <div className="flex justify-center py-20"><div className="spinner"></div></div>
          ) : messages.length === 0 ? (
            <div className="text-center py-20">
              <MessageCircle className="w-12 h-12 text-[#27272A] mx-auto mb-3" />
              <p className="text-[#6A7280]">Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isMine = msg.sender_id === user?.user_id;
              return (
                <div key={msg.message_id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`msg-${msg.message_id}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    isMine 
                      ? 'bg-[#EC008C] text-[#000000] rounded-br-sm' 
                      : 'bg-[#0A0E18] border border-[#1A2030] text-white rounded-bl-sm'
                  }`}>
                    {!isMine && <p className="text-xs font-semibold text-[#EC008C] mb-1">{msg.sender_name}</p>}
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                    <p className={`text-xs mt-1 ${isMine ? 'text-[#000000]/50' : 'text-[#6A7280]'}`}>
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input */}
      <div className="fixed bottom-0 left-0 right-0 glass-strong border-t border-[#1A2030]">
        <div className="max-w-3xl mx-auto px-6 py-3">
          {attachmentUrl && (
            <div className="mb-2 inline-flex items-center gap-2 bg-[#0A0E18] border border-[#1A2030] rounded-lg p-2" data-testid="dm-attachment-preview">
              <img src={attachmentUrl} alt="preview" className="w-12 h-12 object-cover rounded" />
              <button onClick={() => setAttachmentUrl(null)} className="text-[#8A92A0] hover:text-white"><X className="w-4 h-4" /></button>
            </div>
          )}
          <div className="flex gap-3 items-center">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAttachImage} className="hidden" data-testid="dm-file-input" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending}
              className="text-[#8A92A0] hover:text-[#EC008C] p-2 rounded-full disabled:opacity-50"
              title="Attach image"
              data-testid="dm-attach-btn"
            >
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
            </button>
            <input
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
              placeholder="Type a message..."
              className="flex-1 bg-[#0A0E18] border border-[#1A2030] rounded-full px-5 py-3 text-white placeholder-[#71717A] focus:outline-none focus:border-[#EC008C]/50"
              data-testid="message-input"
            />
            <Button
              onClick={sendMessage}
              disabled={(!messageText.trim() && !attachmentUrl) || sending}
              className="bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] rounded-full w-12 h-12 p-0"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
